import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const baseUrl = '/secure/policies/purview/retention-compliance-rule'

export const purviewRetentionRuleApi = createApi({
  reducerPath: 'purviewRetentionRule',
  tagTypes: ['RetentionRule', 'RetentionRules'],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getRetentionRules: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId },
      }),
      transformResponse: (response) => response ?? [],
      providesTags: (result, error, customerId) => [{ type: 'RetentionRules', id: customerId }],
    }),
    getRetentionRulesByPolicy: builder.query({
      query: ({ customerId, policyReferenceId }) => ({
        url: baseUrl,
        params: { customerId, policyReferenceId },
      }),
      transformResponse: (response) => response ?? [],
      providesTags: (result, error, { customerId, policyReferenceId }) => [
        { type: 'RetentionRules', id: customerId },
        { type: 'RetentionRules', id: policyReferenceId },
      ],
    }),
    getRetentionRuleById: builder.query({
      query: ({ customerId, ruleId }) => ({
        url: `${baseUrl}/${ruleId}`,
        params: { customerId },
      }),
      transformResponse: (response) => {
        const { rawDataFlat, ...rest } = response ?? {}
        return { ...rawDataFlat, ...rest }
      },
      providesTags: (result, error, { ruleId }) => [{ type: 'RetentionRule', id: ruleId }],
    }),
  }),
})

export const {
  useGetRetentionRulesQuery,
  useLazyGetRetentionRulesQuery,
  useGetRetentionRulesByPolicyQuery,
  useLazyGetRetentionRulesByPolicyQuery,
  useGetRetentionRuleByIdQuery,
  useLazyGetRetentionRuleByIdQuery,
} = purviewRetentionRuleApi

export const useGetRetentionRules = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionRulesQuery(customerId, { ...options, skip: !customerId || options.skip })
}

export const useGetRetentionRulesByPolicy = ({ policyReferenceId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionRulesByPolicyQuery(
    { customerId, policyReferenceId },
    { ...options, skip: !customerId || !policyReferenceId || options.skip },
  )
}

export const useGetRetentionRuleById = ({ ruleId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionRuleByIdQuery(
    { customerId, ruleId },
    { ...options, skip: !customerId || !ruleId || options.skip },
  )
}
